import { Plus, Trash2 } from "lucide-react";
import { RichTextDisplay } from "./RichTextDisplay";

/**
 * Renders one form field across three modes:
 *  - "fill"    interactive input bound to value/onChange (member filling in)
 *  - "print"   read-only, clean printable output (blank-form or completed)
 *  - "preview" same as fill but used inside the Admin builder's live preview
 *
 * Shared by AdminFormBuilder (preview tab), FormFill and the print views —
 * single place that understands every field type in backend/forms.py.
 */
export function FormFieldRenderer({ field, value, onChange, mode = "fill" }) {
  if (field.type === "heading") {
    return (
      <h3 className="font-display text-lg font-semibold mt-6 mb-1 first:mt-0" data-testid={`form-field-heading-${field.field_id}`}>
        {field.label}
      </h3>
    );
  }

  if (field.type === "text_block") {
    return (
      <div className="text-sm text-ink-muted bg-slate-50 border border-slate-100 rounded-lg p-3" data-testid={`form-field-text-block-${field.field_id}`}>
        {field.label && <p className="font-medium text-ink mb-1">{field.label}</p>}
        <RichTextDisplay content={field.help_text} />
      </div>
    );
  }

  if (mode === "print") {
    return <PrintField field={field} value={value} />;
  }

  const disabled = mode === "preview";

  if (field.type === "repeatable_group") {
    return <RepeatableField field={field} value={value} onChange={onChange} disabled={disabled} />;
  }

  return renderAnswerField(field, value, onChange, disabled);
}

// Plain (lowercase) helper — deliberately NOT a component so it can be
// called from both FormFieldRenderer and RepeatableField without the
// two forming a mutually-recursive pair of exported components, which
// tripped a webpack/react-refresh "Maximum call stack size exceeded"
// build error in this dev setup.
function renderAnswerField(field, value, onChange, disabled) {
  const id = `field-${field.field_id}`;
  const inputCls = "mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal/30 print:border-0 print:border-b print:rounded-none print:px-0";

  const Label = () => (
    <label htmlFor={id} className="block text-sm font-medium text-ink">
      {field.label}
      {field.required && <span className="text-red-600 ml-0.5">*</span>}
    </label>
  );
  const HelpText = () =>
    field.help_text ? <p className="text-xs text-ink-soft mt-0.5">{field.help_text}</p> : null;

  switch (field.type) {
    case "short_text":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <input id={id} type="text" value={value || ""} placeholder={field.placeholder}
                 onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "long_text":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <textarea id={id} rows={3} value={value || ""} placeholder={field.placeholder}
                    onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "number":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <input id={id} type="number" value={value ?? ""} placeholder={field.placeholder}
                 onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "date":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <input id={id} type="date" value={value || ""} onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "time":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <input id={id} type="time" value={value || ""} onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "datetime":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <input id={id} type="datetime-local" value={value || ""} onChange={(e) => onChange?.(e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      );
    case "checkbox":
      return (
        <label className="flex items-start gap-2 text-sm" data-testid={`form-field-${field.field_id}`}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange?.(e.target.checked)} disabled={disabled} className="mt-0.5" />
          <span>{field.label}{field.required && <span className="text-red-600 ml-0.5">*</span>}</span>
        </label>
      );
    case "checkbox_group":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <div className="mt-1.5 flex flex-wrap gap-3">
            {(field.options || []).map((opt) => {
              const checked = Array.isArray(value) && value.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox" checked={checked} disabled={disabled}
                    onChange={(e) => {
                      const cur = Array.isArray(value) ? value : [];
                      onChange?.(e.target.checked ? [...cur, opt] : cur.filter((v) => v !== opt));
                    }}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </div>
      );
    case "radio_group":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <div className="mt-1.5 flex flex-wrap gap-3">
            {(field.options || []).map((opt) => (
              <label key={opt} className="flex items-center gap-1.5 text-sm">
                <input type="radio" name={id} checked={value === opt} disabled={disabled} onChange={() => onChange?.(opt)} />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    case "dropdown":
      return (
        <div data-testid={`form-field-${field.field_id}`}>
          <Label /><HelpText />
          <select id={id} value={value || ""} disabled={disabled} onChange={(e) => onChange?.(e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      );
    default:
      return null;
  }
}

function RepeatableField({ field, value, onChange, disabled }) {
  const rows = Array.isArray(value) && value.length ? value : [{}];

  const setRow = (idx, subId, v) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [subId]: v } : r));
    onChange?.(next);
  };
  const addRow = () => onChange?.([...rows, {}]);
  const removeRow = (idx) => onChange?.(rows.filter((_, i) => i !== idx));

  return (
    <div data-testid={`form-field-${field.field_id}`}>
      <label className="block text-sm font-medium text-ink">
        {field.label}{field.required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {field.help_text && <p className="text-xs text-ink-soft mt-0.5">{field.help_text}</p>}
      <div className="mt-2 space-y-3">
        {rows.map((row, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 p-3 grid sm:grid-cols-3 gap-3 relative" data-testid={`form-field-${field.field_id}-row-${idx}`}>
            {(field.sub_fields || []).map((sub) => (
              <div key={sub.field_id}>
                {renderAnswerField({ ...sub, required: false }, row[sub.field_id], (v) => setRow(idx, sub.field_id, v), disabled)}
              </div>
            ))}
            {!disabled && rows.length > 1 && (
              <button type="button" onClick={() => removeRow(idx)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-200 text-ink-soft hover:text-red-600 flex items-center justify-center"
                      data-testid={`form-field-${field.field_id}-remove-row-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <button type="button" onClick={addRow} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-teal" data-testid={`form-field-${field.field_id}-add-row`}>
          <Plus className="w-4 h-4" /> Add entry
        </button>
      )}
    </div>
  );
}

function PrintField({ field, value }) {
  const line = (content) => (
    <div className="text-sm border-b border-slate-300 min-h-[1.4rem] py-0.5">{content ?? "\u00A0"}</div>
  );
  return (
    <div className="mb-3" data-testid={`form-print-field-${field.field_id}`}>
      <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
        {field.label}{field.required ? " *" : ""}
      </div>
      {field.type === "checkbox" ? (
        <div className="text-sm">{value ? "☑ Yes" : "☐ No"}</div>
      ) : field.type === "checkbox_group" ? (
        <div className="text-sm flex flex-wrap gap-3">
          {(field.options || []).map((opt) => (
            <span key={opt}>{Array.isArray(value) && value.includes(opt) ? "☑" : "☐"} {opt}</span>
          ))}
        </div>
      ) : field.type === "radio_group" || field.type === "dropdown" ? (
        <div className="text-sm flex flex-wrap gap-3">
          {(field.options || []).map((opt) => (
            <span key={opt}>{value === opt ? "●" : "○"} {opt}</span>
          ))}
        </div>
      ) : field.type === "repeatable_group" ? (
        <table className="w-full text-sm border-collapse mt-1">
          <thead>
            <tr>{(field.sub_fields || []).map((s) => (
              <th key={s.field_id} className="border border-slate-300 px-2 py-1 text-left text-xs">{s.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(Array.isArray(value) && value.length ? value : [{}]).map((row, idx) => (
              <tr key={idx}>
                {(field.sub_fields || []).map((s) => (
                  <td key={s.field_id} className="border border-slate-300 px-2 py-1 min-h-[1.4rem]">{row[s.field_id] ?? "\u00A0"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        line(value)
      )}
    </div>
  );
}
